import ReactECharts from 'echarts-for-react';
import EChartsReact, { EChartsInstance } from 'echarts-for-react';
import { EChartsOption, SeriesOption } from 'echarts';
import moment, { Duration } from 'moment';
import { differenceInHours } from 'date-fns';
import React, { useRef, useState } from 'react';
import { getDLSLineColors } from './graph-colors';
import { format, differenceInMinutes } from 'date-fns';
import { CallbackDataParams, YAXisOption } from 'echarts/types/dist/shared';
import { computeDomain, isAllDataEmpty, markerSize } from './graph-utils';

interface BaseAxisSettings {
  mapValue?: (v: any, idx: number, data: any[]) => any;
  name: string;
}

export interface YAxisSettings extends BaseAxisSettings {
  dataKeys: string[];
  id: string;
  hideMarkers?: boolean;
  hideMarkersAtThreshold?: Duration;
  hideQualityMarkers?: boolean;
  mappings?: Record<string | number, string>;
  graphType: 'line' | 'bar';
  axisType: 'value' | 'category';
  unit?: string;
  domainStart?: number;
  domainEnd?: number;
  defaultDisabled?: boolean;
}

export interface XAxisSettings extends BaseAxisSettings {
  dataKey: string;
  hide?: boolean;
}

export interface GraphSettings {
  xAxis: XAxisSettings;
  yAxes: YAxisSettings[];
  qualityKey: string;
  description?: string;
  groupBy?: string;
  grid?: EChartsOption['grid'];
}

export interface GraphProps {
  data: Record<string, any>[];
  settings: GraphSettings;
  onZoom: (range: [Date, Date]) => void;
  onResetZoom: () => void;
  zoomRange: [Date, Date];
  disableTitle?: boolean;
  loading?: boolean;
  maximumDataPoints?: number;
  connectNulls?: boolean;
}

const MARGIN_BETWEEN_AXES = 80;

export const SingleGraph: React.FC<GraphProps> = ({
  zoomRange,
  maximumDataPoints,
  data,
  onZoom,
  onResetZoom,
  settings: { qualityKey, yAxes, xAxis, description, groupBy, grid = {} },
  loading,
  connectNulls,
}) => {
  const ref = useRef<EChartsReact>(null);
  const [selectedAxes, setSelectedAxes] = useState<string[]>(
    yAxes.filter((y) => !y.defaultDisabled).map((y) => y.name),
  );

  const qualities = data.map((d) => d[qualityKey]);
  const colors = getDLSLineColors(yAxes.flatMap((y) => y.dataKeys).length);
  const centerText =
    isAllDataEmpty(xAxis.dataKey, data) && !loading ? 'No data available' : undefined;
  const title = centerText
    ? {
        show: true,
        textStyle: {
          color: 'grey',
          fontSize: 16,
        },
        text: centerText,
        left: 'center',
        top: 'center',
      }
    : {
        show: true,
        text: description,
        top: -5,
        textStyle: {
          fontSize: 14,
        },
      };

  const multipleDaysOfData = differenceInHours(zoomRange[1], zoomRange[0]) > 24;
  const options: EChartsOption = {
    title,
    toolbox: {
      feature: {
        dataZoom: {
          yAxisIndex: 'none',
          icon: {
            zoom: 'path://', // hack to remove zoom button
            back: 'path://', // hack to remove restore button
          },
        },
      },
    },
    grid: {
      left: 70,
      top: 40,
      right: 64 + Math.max((selectedAxes.length - 2) * MARGIN_BETWEEN_AXES, 0),
      bottom: 32,
      containLabel: !!description,
      ...grid,
    },
    dataZoom: [
      {
        type: 'inside',
        throttle: 50,
        startValue: zoomRange?.[0],
        endValue: zoomRange?.[1],
        zoomLock: true,
        preventDefaultMouseMove: false,
      },
    ],
    legend: {
      show: yAxes.flatMap((y) => y.dataKeys).length > 2,
    },
    tooltip: {
      trigger: yAxes.some((y) => y.mappings) ? 'item' : 'axis',
    },
    xAxis: {
      type: 'time',
      min: zoomRange?.[0],
      max: zoomRange?.[1],
      axisLine: {
        show: !xAxis.hide,
        onZero: false,
      },
      minorTick: {
        show: !xAxis.hide,
      },
      minorSplitLine: {
        show: true,
      },
      axisTick: {
        show: !xAxis.hide,
        alignWithLabel: true,
      },
      axisLabel: {
        show: !xAxis.hide,
        showMaxLabel: true,
        showMinLabel: true,
        formatter: (value: number, index: number) => {
          const date = new Date(value);
          if (differenceInMinutes(zoomRange[1], zoomRange[0]) < 10) {
            return format(date, 'HH:mm:ss');
          }

          if (
            index === 0 ||
            (multipleDaysOfData && date.getHours() === 23 && date.getMinutes() === 59) // Last data point within time range
          ) {
            return format(date, "dd MMM'\n'HH:mm");
          }

          if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
            return format(date, 'dd MMM');
          }

          return format(date, 'HH:mm');
        },
      },
    },
    yAxis: yAxes.map((y) => {
      const domainStart =
        y.domainStart === undefined && data
          ? computeDomain('start', data as any, zoomRange, y.dataKeys)
          : y.domainStart;

      const idxOfSelectedAxes = yAxes.filter((y) => selectedAxes.includes(y.name)).indexOf(y);
      const baseOptions = {
        name: `${y.name}\n{unit|${y.unit ? `(${y.unit})` : ''}}`,
        ...(title && idxOfSelectedAxes === 0 && groupBy
          ? { nameLocation: 'center', nameGap: 80, nameRotate: 0 }
          : {}),
        show: selectedAxes.includes(y.name),
        nameTextStyle: {
          rich: {
            unit: {
              fontSize: 10,
              fontWeight: 'bolder',
              height: 14,
            },
          },
        },
        splitLine: {
          show: true,
        },
        alignTicks: true,
        offset: idxOfSelectedAxes >= 2 ? MARGIN_BETWEEN_AXES * (idxOfSelectedAxes - 1) : 0,
        axisLine: { onZero: false, show: false },
        axisTick: { show: true },
      } as YAXisOption;

      if (y.mappings) {
        return {
          ...baseOptions,
          type: 'category',
          boundaryGap: true,
          data: Object.values(y.mappings),
          axisTick: {
            alignWithLabel: true,
          },
        } as YAXisOption;
      }

      return {
        ...baseOptions,
        type: 'value',
        minorSplitLine: {
          show: true,
        },
        min: domainStart,
        max: y.domainEnd,
      };
    }),
    series: yAxes.flatMap((yAxis, yAxisIndex) => {
      return yAxis.dataKeys.map((dataKey, dataKeyIdx) => {
        const hideMarkers =
          yAxis.hideMarkers ||
          (yAxis.hideMarkersAtThreshold &&
            zoomRange &&
            moment.duration(moment(zoomRange[1]).diff(moment(zoomRange[0]))) >
              yAxis.hideMarkersAtThreshold);

        return {
          id: dataKey,
          name: dataKey,
          type: yAxis.graphType,
          symbol: hideMarkers ? 'none' : 'circle',
          symbolSize: data && markerSize(data, zoomRange, maximumDataPoints),
          step: yAxis.mappings ? 'start' : undefined,
          alignTicks: true,
          connectNulls,
          minorSplitLine: {
            show: true,
          },
          color:
            yAxis.dataKeys.length === 1 && !yAxis.hideQualityMarkers
              ? 'rgba(220, 220, 220, 80)'
              : colors[yAxisIndex + dataKeyIdx],
          emphasis: {
            disabled: true,
          },
          itemStyle: hideMarkers
            ? undefined
            : {
                color:
                  !yAxis.hideQualityMarkers && qualities
                    ? (o: CallbackDataParams) => {
                        switch (Math.round(qualities[o.dataIndex])) {
                          case 0:
                            return 'black';
                          case 1:
                            return 'red';
                          case 2:
                            return 'orange';
                          case 3:
                            return '#7fb204';
                          case 4:
                            return 'green';
                          default:
                            return 'purple';
                        }
                      }
                    : colors[yAxisIndex + dataKeyIdx],
              },
          yAxisIndex,
          animation: false,
          data: data?.map((d) => [d[xAxis.dataKey], d[dataKey]]),
        } as SeriesOption;
      });
    }),
  };

  return (
    <ReactECharts
      ref={ref}
      showLoading={loading && !data?.length}
      option={options}
      notMerge={true}
      lazyUpdate={true}
      onChartReady={(instance: EChartsInstance) => {
        instance.dispatchAction({
          type: 'takeGlobalCursor',
          key: 'dataZoomSelect',
          dataZoomSelectActive: true,
        });

        instance.getDom().ondblclick = (e: MouseEvent) => {
          if (e.target) {
            const computed = window.getComputedStyle(e.target as Element)['cursor'];
            // Only reset zoom if you didn't double click a btn
            if (computed !== 'pointer') {
              onResetZoom();
            }
          }
        };

        // This is cumbersome, but necessary
        // When toggling legend items, we dynamically hide/show y-axes and apply the correct margins to the chart
        // this causes the chart to re-render and the legend state gets lost... therefore we effectively now "control" the graph state
        // by dispatching actions and listening to the change events
        yAxes
          .filter((y) => !selectedAxes.includes(y.name))
          .forEach((y) => {
            instance.dispatchAction({
              type: 'legendUnSelect',
              name: y.name,
            });
          });
      }}
      onEvents={{
        restore: onResetZoom,
        legendselectchanged: ({ name }: { name: string }) => {
          if (selectedAxes.includes(name)) {
            setSelectedAxes(selectedAxes.filter((s) => s !== name));
          } else {
            setSelectedAxes([...selectedAxes, name]);
          }
        },
        dataZoom: (e: any) => {
          const { startValue, endValue } = e.batch[0];
          if (startValue && endValue) {
            onZoom([new Date(startValue), new Date(endValue)]);
          }
        },
      }}
    />
  );
};
